import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './index.css';
import { WeatherData } from './types';
import { fetchWeather } from './services/weather';
import { capitalizeWords } from './utils/stringUtils';
import { Analytics } from "@vercel/analytics/react";

const iconMap: { [key: string]: string } = {
  'langit cerah': 'CLEAR_DAY',
  'awan cerah': 'PARTLY_CLOUDY_DAY',
  'awan mendung': 'PARTLY_CLOUDY_NIGHT',
  'beberapa awan': 'PARTLY_CLOUDY_DAY',
  'awan terpisah': 'PARTLY_CLOUDY_DAY',
  'awan terpecah': 'CLOUDY',
  'sedikit berawan': 'PARTLY_CLOUDY_DAY',
  'hujan gerimis': 'RAIN',
  'hujan ringan': 'RAIN',
  'hujan sedang': 'RAIN',
  'hujan lebat': 'RAIN',
  'hujan sangat lebat': 'RAIN',
  'hujan ekstrim': 'RAIN',
  'hujan es': 'SLEET',
  'gerimis ringan': 'RAIN',
  'gerimis sedang': 'RAIN',
  'gerimis lebat': 'RAIN',
  'badai petir': 'SLEET',
  'badai petir ringan': 'SLEET',
  'badai petir sedang': 'SLEET',
  'badai petir kuat': 'SLEET',
  'badai petir sangat kuat': 'SLEET',
  'badai petir ekstrim': 'SLEET',
  'badai petir disertai hujan gerimis': 'RAIN',
  'badai petir disertai hujan ringan': 'RAIN',
  'badai petir disertai hujan lebat': 'RAIN',
  'salju ringan': 'SNOW',
  'salju': 'SNOW',
  'salju lebat': 'SNOW',
  'hujan salju': 'SNOW',
  'hujan salju ringan': 'SNOW',
  'hujan salju lebat': 'SNOW',
  'badai salju': 'SNOW',
  'kabut': 'FOG',
  'kabut asap': 'FOG',
  'asap': 'FOG',
  'debu': 'FOG',
  'pasir': 'FOG',
  'abu': 'FOG',
  'kabut tebal': 'FOG',
  'badai angin': 'WIND',
  'tornado': 'WIND',
  'hujan lokal': 'RAIN',
  'gerimis lokal': 'RAIN',
};

const normalizeCondition = (condition: string) => condition.replace(/\s+/g, ' ').trim();

const WeatherIcon = React.memo(({ condition }: { condition: string }) => {
  const ReactAnimatedWeather = React.lazy(() => import('react-animated-weather'));

  return (
    <React.Suspense fallback={<div>Loading...</div>}>
      <ReactAnimatedWeather
        icon={iconMap[normalizeCondition(condition)] || 'CLEAR_DAY'}
        color="white"
        size={64}
        animate={true}
      />
    </React.Suspense>
  );
});

function App() {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [currentTime, setCurrentTime] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const initializeWidget = useCallback(async () => {
    try {
      const weatherData = await fetchWeather();
      setWeather(weatherData);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Unable to load weather data');
    }
  }, []);

  useEffect(() => {
    initializeWidget();
    const intervalId = setInterval(initializeWidget, 15 * 60 * 1000); // Refresh every 15 mins
    return () => clearInterval(intervalId);
  }, [initializeWidget]);

  useEffect(() => {
    const updateTime = () => {
      setCurrentTime(new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }));
    };

    updateTime();
    const timeIntervalId = setInterval(updateTime, 1000);
    return () => clearInterval(timeIntervalId);
  }, []);

  return (
    <div className="min-h-screen relative text-white">
      {/* Gradient Animation Background */}
      <div className="absolute inset-0 bg-gradient-to-r from-red-800 via-orange-900 to-amber-900 animate-gradient-x"></div>
      
      <div className="relative z-10 flex flex-col justify-center items-center h-screen">
        <div className="text-6xl font-bold tracking-wider">
          {currentTime}
        </div>
        {error && (
          <div className="text-red-500 mt-4">{error}</div>
        )}
        {weather && (
          <div className="text-2xl mt-4 bg-black/30 p-4 rounded-lg flex flex-col items-center">
            <span className="ml-2">{weather.temperature}°C | {capitalizeWords(weather.condition)}</span>
            <WeatherIcon condition={weather.condition} />
            <span className="mt-2 text-lg">{weather.location}</span>
          </div>
        )}
      </div>
      <Analytics />
    </div>
  );
}

export default App;